import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { isAdmin } from '../utils/rbac';
import { extractApiError } from '../utils/errorUtils';

interface AdminOnlyGuardProps {
  children: React.ReactNode;
}

/**
 * AdminOnlyGuard - Restricts access to admin users only (not fair managers)
 * Use this for routes that should only be accessible by admins, such as:
 * - User management
 * - Product category management
 * - System settings
 */
const AdminOnlyGuard: React.FC<AdminOnlyGuardProps> = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [userIsAdmin, setUserIsAdmin] = useState(false);

  useEffect(() => {
    const checkAdminStatus = () => {
      try {
        setUserIsAdmin(isAdmin());
      } catch (err: unknown) {
        const apiError = extractApiError(err);
        console.error('Failed to check admin status:', apiError);
        setUserIsAdmin(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkAdminStatus();
  }, []);

  // Show loading spinner while checking
  if (isLoading) {
    return (
      <div className="min-h-screen bg-teal-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Verifying permissions...</p>
        </div>
      </div>
    );
  }

  // Redirect non-admins to fair manager dashboard or login
  if (!userIsAdmin) {
    // If they're a fair manager, send them to fair manager dashboard
    // Otherwise send to login
    return <Navigate to="/fairmanager/dashboard" replace />;
  }

  // User is admin, render children
  return <>{children}</>;
};

export default AdminOnlyGuard;
